/**
 * The estimate-then-correct global view-row index space
 * (design/large-samples.md, "View-row pagination", amendment 1). Row
 * accounting is per event chunk: a chunk's row count is estimated from its
 * stats (filter-aware, minus collapse elision, runs guessed at average
 * length), then corrected to exact when the chunk decodes. The virtualizer
 * sees prefix-summed totals; corrections are O(#chunks) and bounded per
 * chunk. Ordinals — not row indexes — are the stable scroll coordinate
 * (amendment 3): callers re-anchor via `rowIndexForOrdinal`.
 */
import type { SequenceReader } from "./chunkStore";
import {
  decodeRange,
  RUN_TYPES,
  STRUCTURAL_TYPES,
  type DecodeCtx,
  type ViewRow,
} from "./decode";
import { at } from "./format";
import { log } from "./log";
import type { SkeletonIndex } from "./skeletonIndex";
import type { ChunkedEvent, EventChunkStats } from "./types";

/** Guessed events per run row when a chunk is only estimated. */
const EST_RUN_LEN = 6;

export type RowSlot =
  | { kind: "row"; row: ViewRow; globalIndex: number }
  | {
      kind: "placeholder";
      chunkIdx: number;
      estOrdinal: number;
      globalIndex: number;
    };

export class RowSpace {
  readonly chunkRows: number[];
  readonly exact: boolean[];
  // No eviction (unlike the byte store / parsed-chunk LRU): decoded rows
  // accumulate for the panel's lifetime. Known gap — a cap that drops
  // far-away chunks' rows while keeping chunkRows/exact would preserve
  // exact accounting (design/large-samples.md deferred issues).
  private materializedRows = new Map<number, ViewRow[]>();
  private inflight = new Map<number, Promise<void>>();
  private prefix: number[] = [];
  version = 0;
  private listeners = new Set<() => void>();

  /** Merged elided ordinal ranges `[lo, hi]` (collapsed span interiors). */
  private elision: [number, number][] = [];

  constructor(
    private events: SequenceReader<ChunkedEvent>,
    private stats: readonly EventChunkStats[],
    private skel: SkeletonIndex,
    collapsed: ReadonlySet<string>,
    private visible: (type: string) => boolean
  ) {
    // elision: interiors of collapsed spans + full extents of filter-empty spans
    const ranges: [number, number][] = [];
    skel.spans.forEach((span, i) => {
      if (!skel.hasVisibleContents(i, visible)) {
        ranges.push([span.begin, span.extent[1]]);
      } else if (collapsed.has(span.id)) {
        ranges.push([span.begin + 1, span.extent[1]]);
      }
    });
    ranges.sort((a, b) => a[0] - b[0]);
    for (const range of ranges) {
      const last = this.elision[this.elision.length - 1];
      if (last && range[0] <= last[1] + 1) {
        last[1] = Math.max(last[1], range[1]);
      } else {
        this.elision.push([range[0], range[1]]);
      }
    }

    this.chunkRows = this.stats.map((chunkStats, c) =>
      this.estimateChunk(chunkStats, c)
    );
    this.exact = this.stats.map(
      (_, c) => this.chunkRows[c] === 0 && this.fullyElided(c)
    );
    this.recompute();
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  get total(): number {
    return this.prefix[this.prefix.length - 1] ?? 0;
  }

  private chunkBounds(c: number): [number, number] {
    return this.events.chunkBounds(c);
  }

  private elidedOverlap(lo: number, hi: number): number {
    let covered = 0;
    for (const [a, b] of this.elision) {
      const start = Math.max(lo, a);
      const end = Math.min(hi - 1, b);
      if (start <= end) {
        covered += end - start + 1;
      }
    }
    return covered;
  }

  private fullyElided(c: number): boolean {
    const [lo, hi] = this.chunkBounds(c);
    return this.elidedOverlap(lo, hi) >= hi - lo;
  }

  private estimateChunk(chunkStats: EventChunkStats, c: number): number {
    const [lo, hi] = this.chunkBounds(c);
    const visibleFrac = 1 - this.elidedOverlap(lo, hi) / (hi - lo);
    if (visibleFrac <= 0) {
      return 0;
    }
    let typeRows = 0;
    let runRows = 0;
    for (const [type, count] of Object.entries(chunkStats.type_counts)) {
      if (STRUCTURAL_TYPES.has(type) || !this.visible(type)) {
        continue;
      }
      if (RUN_TYPES.has(type)) {
        runRows += Math.max(1, Math.round(count / EST_RUN_LEN));
      } else {
        typeRows += count;
      }
    }
    // structural span rows are exact: spans beginning in this chunk, begin not elided
    const spanRows = this.skel.spans.filter(
      (span, i) =>
        span.begin >= lo &&
        span.begin < hi &&
        this.elidedOverlap(span.begin, span.begin + 1) === 0 &&
        this.skel.hasVisibleContents(i, this.visible)
    ).length;
    return Math.round((typeRows + runRows) * visibleFrac) + spanRows;
  }

  private recompute(): void {
    this.prefix = [0];
    for (const n of this.chunkRows) {
      this.prefix.push((this.prefix.at(-1) ?? 0) + n);
    }
    this.version += 1;
    this.listeners.forEach((fn) => fn());
  }

  slotAt(globalIndex: number): RowSlot {
    // binary search: greatest c with prefix[c] <= globalIndex
    let lo = 0;
    let hi = this.chunkRows.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (at(this.prefix, mid) <= globalIndex) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    const offset = globalIndex - at(this.prefix, lo);
    const row = this.materializedRows.get(lo)?.[offset];
    if (row !== undefined) {
      return { kind: "row", row, globalIndex };
    }
    const [clo, chi] = this.chunkBounds(lo);
    const est = Math.max(at(this.chunkRows, lo), 1);
    const estOrdinal = Math.min(
      chi - 1,
      clo + Math.floor(((offset + 0.5) / est) * (chi - clo))
    );
    return { kind: "placeholder", chunkIdx: lo, estOrdinal, globalIndex };
  }

  /**
   * The ordinal anchor: map an event ordinal to its global row index.
   *
   * Two accepted one-row inaccuracies on materialized chunks: an ordinal
   * covered by a run row that STARTS in the previous chunk anchors one row
   * late (the containing run row lives in that chunk), and an ordinal past
   * the chunk's last row (e.g. a trailing span_end) clamps backward to it.
   */
  rowIndexForOrdinal(ordinal: number): number {
    const c = this.events.chunkIndexOf(
      Math.min(ordinal, this.events.knownCount - 1)
    );
    const rows = this.materializedRows.get(c);
    if (rows) {
      const i = rows.findIndex((row) => row.ordinal >= ordinal);
      return at(this.prefix, c) + (i === -1 ? Math.max(rows.length - 1, 0) : i);
    }
    const [lo, hi] = this.chunkBounds(c);
    return (
      at(this.prefix, c) +
      Math.floor(((ordinal - lo) / (hi - lo)) * at(this.chunkRows, c))
    );
  }

  isMaterialized(chunkIdx: number): boolean {
    return this.materializedRows.has(chunkIdx);
  }

  /** Decode a chunk's window and correct its row count; idempotent. */
  materialize(chunkIdx: number, ctx: DecodeCtx): Promise<void> {
    if (this.materializedRows.has(chunkIdx)) {
      return Promise.resolve();
    }
    let pending = this.inflight.get(chunkIdx);
    if (!pending) {
      pending = this.doMaterialize(chunkIdx, ctx).finally(() =>
        this.inflight.delete(chunkIdx)
      );
      this.inflight.set(chunkIdx, pending);
    }
    return pending;
  }

  private async doMaterialize(chunkIdx: number, ctx: DecodeCtx): Promise<void> {
    const [lo, hi] = this.chunkBounds(chunkIdx);
    const estimated = at(this.chunkRows, chunkIdx);
    // decode starts at the first non-elided ordinal in the chunk
    let start = lo;
    for (const [a, b] of this.elision) {
      if (a <= start && start <= b) {
        start = b + 1;
      }
    }
    let rows: ViewRow[] = [];
    if (start < hi) {
      const prev = this.stats[chunkIdx - 1];
      const cur = this.stats[chunkIdx];
      const headRunContinues =
        start === lo &&
        prev !== undefined &&
        cur !== undefined &&
        RUN_TYPES.has(prev.last.type) &&
        prev.last.type === cur.first.type &&
        (prev.last.span_id ?? null) === (cur.first.span_id ?? null);
      rows = await decodeRange(ctx, start, hi, headRunContinues);
    }
    this.materializedRows.set(chunkIdx, rows);
    this.chunkRows[chunkIdx] = rows.length;
    this.exact[chunkIdx] = true;
    log.info(
      `materialize events chunk ${chunkIdx} [${lo},${hi}) → ${rows.length} rows` +
        ` (estimate was ${estimated}${start > lo ? `, decode from ${start} past elision` : ""})`
    );
    this.recompute();
  }

  get materializedCount(): number {
    return this.materializedRows.size;
  }
}
